/**
 * Landlord & Agent workspace — routes and capability slugs.
 *
 * Paths come from `docs/v2/route-map.md`, which is the contract between phases: a route exists
 * there before it is built, so two phases cannot invent different paths for the same screen.
 * Route builders live here so a link is never a string literal in a component.
 */
export const WORKSPACE_KEY = 'landlord'

export const ROUTES = {
  overview: '/app/landlord',
  properties: '/app/landlord/properties',
  propertyNew: '/app/landlord/properties/new',
  property: (id: number | string) => `/app/landlord/properties/${id}`,
  /**
   * Editing a property is the onboarding flow again (owned by `modules/onboarding`): the
   * property's fields, then any spaces to add. `step` opens a particular step — `structure`
   * where the page's ask is "add spaces".
   */
  propertyEdit: (
    id: number | string,
    step: 'details' | 'structure' | 'rent' | 'review' = 'details',
  ) => `/app/landlord/properties/${id}/edit/${step}`,
  spaces: '/app/landlord/spaces',
  space: (id: number | string) => `/app/landlord/spaces/${id}`,
  tenants: '/app/landlord/tenants',
  tenant: (id: number | string) => `/app/landlord/tenants/${id}`,
  /** Phase 07. Owned by `modules/tenancy`; the paths live in this workspace. */
  tenantNew: (spaceId: number | string) => `/app/landlord/spaces/${spaceId}/tenants/new`,
  renewals: '/app/landlord/renewals',
  payments: '/app/landlord/payments',
  payment: (id: string) => `/app/landlord/payments/${id}`,
  estates: '/app/landlord/estates',
  estate: (id: number | string) => `/app/landlord/estates/${id}`,
  settingsProfile: '/app/landlord/settings/profile',
  settingsPayout: '/app/landlord/settings/payout',
} as const

/**
 * Capability slugs, mirroring `identity/constants.py::LANDLORD_CAPABILITIES`.
 *
 * **UX only**: every one is enforced again by a permission class and a selector scope on the
 * server. Hiding a button is not a control (architecture.md §3).
 */
export const CAPABILITIES = {
  propertyView: 'property.view',
  propertyCreate: 'property.create',
  propertyUpdate: 'property.update',
  propertyDelete: 'property.delete',
  spaceView: 'space.view',
  spaceManage: 'space.manage',
  mediaManage: 'media.manage',
  estateView: 'estate.view',
  estateManage: 'estate.manage',
  tenantView: 'tenant.view',
  tenantManage: 'tenant.manage',
  rentView: 'rent.view',
} as const

/**
 * The fields `POST /spaces/bulk-update/` accepts, mirroring
 * `property_domain/constants.py::BULK_SPACE_FIELDS`. The server refuses anything else, so this
 * list only decides what the dialog offers.
 */
export const BULK_SPACE_FIELDS = [
  'price',
  'space_type',
  'advertise',
  'description',
  'is_rent_exempt',
  'max_upfront_sessions',
] as const

/** Rent status values, mirroring `property_domain/constants.py::RentStatus`. */
export const RENT_STATUS = {
  vacant: 'vacant',
  exempt: 'exempt',
  active: 'active',
  expiringSoon: 'expiring_soon',
  expired: 'expired',
  overdue: 'overdue',
  pendingPayment: 'pending_payment',
} as const

/**
 * Rent status → the key `ApmpStatusTag` understands.
 *
 * One mapping, app-wide, so "Overdue" is never red on one screen and orange on another
 * (frontend phase-04 §3.7).
 */
export const RENT_STATUS_TAG: Record<string, string> = {
  active: 'ACTIVE',
  expiring_soon: 'EXPIRING',
  expired: 'EXPIRED',
  overdue: 'OVERDUE',
  pending_payment: 'PENDING_PAYMENT',
  exempt: 'EXEMPT',
  vacant: 'VACANT',
}
